import { Link, useLocation } from "react-router-dom";
import { Printer, Scale } from "lucide-react";
import { LEGAL, LEGAL_DOCS } from "@/legal/config";
import { CopyrightNotice } from "@/components/legal/CopyrightNotice";

export function LegalLayout({ slug, title, intro, children, sections = [] }) {
  const { pathname } = useLocation();
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid={`legal-${slug}-page`}>
      <nav className="flex flex-wrap gap-2 text-xs" data-testid="legal-doc-switcher">
        {LEGAL_DOCS.map((d) => (
          <Link key={d.slug} to={d.path} data-testid={`legal-nav-${d.slug}`}
            className={`px-3 py-1.5 rounded-full border transition-colors ${pathname === d.path ? "border-white bg-white text-black font-bold" : "border-white/10 text-neutral-400 hover:text-white hover:border-white/30"}`}>
            {d.short}
          </Link>
        ))}
      </nav>
      <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF9F0A]/40 bg-[#FF9F0A]/10">
        <Scale className="h-3.5 w-3.5 text-[#FF9F0A]" />
        <span className="text-xs font-semibold text-[#FF9F0A] uppercase tracking-widest">{LEGAL.brand} legal · v{LEGAL.version}</span>
      </div>
      <h1 className="mt-4 font-black tracking-tight text-4xl sm:text-5xl" data-testid="legal-title">{title}<span className="text-[#FF3B30]">.</span></h1>
      <p className="mt-3 text-sm text-neutral-500">Effective {LEGAL.effective} · Operated by {LEGAL.entity}, {LEGAL.entityDescription}.</p>
      {intro && <p className="mt-4 max-w-3xl text-neutral-300 leading-relaxed">{intro}</p>}
      <div className="mt-10 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12">
        <aside className="hidden lg:block">
          <div className="sticky top-24 space-y-1 text-xs" data-testid="legal-toc">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">On this page</div>
            {sections.map((s, i) => (
              <a key={s.id} href={`#${s.id}`} className="block py-1 text-neutral-400 hover:text-white truncate">{i + 1}. {s.t}</a>
            ))}
            <button onClick={() => window.print()} className="mt-4 inline-flex items-center gap-1.5 text-neutral-500 hover:text-white" data-testid="legal-print-btn"><Printer className="h-3.5 w-3.5" /> Print / save PDF</button>
          </div>
        </aside>
        <article className="space-y-10 max-w-3xl">{children}
          <footer className="pt-8 border-t border-white/10 text-xs text-neutral-500 space-y-3">
            <CopyrightNotice compact />
            <p>Questions: <a className="text-[#007AFF] hover:underline" href={`mailto:${LEGAL.emails.legal}`}>{LEGAL.emails.legal}</a> · Support: <a className="text-[#007AFF] hover:underline" href={`mailto:${LEGAL.emails.support}`}>{LEGAL.emails.support}</a></p>
            <p>Governing law: {LEGAL.governingLaw}. Venue: {LEGAL.courts}. Consumers in the EU/UK keep the mandatory protections of their home country.</p>
            <p>See all documents in the <Link to="/legal-center" className="underline hover:text-neutral-300">Legal Center</Link>.</p>
          </footer>
        </article>
      </div>
    </div>
  );
}

export function Sec({ id, n, t, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`legal-section-${id}`}>
      <h2 className="text-base md:text-lg font-black tracking-tight text-white">{n}. {t}</h2>
      <div className="mt-3 space-y-3 text-sm text-neutral-300 leading-relaxed">{children}</div>
    </section>
  );
}

export function Ul({ items }) {
  return <ul className="list-disc pl-5 space-y-1.5">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>;
}

export function Hi({ children }) {
  return <span className="text-white font-semibold">{children}</span>;
}

export function Box({ tone = "info", children, ...rest }) {
  const c = { info: "border-[#007AFF]/40 bg-[#007AFF]/10", warn: "border-[#FF9F0A]/40 bg-[#FF9F0A]/10", ok: "border-[#34C759]/40 bg-[#34C759]/10" }[tone];
  return <div className={`rounded-xl border p-4 text-sm text-neutral-200 ${c}`} {...rest}>{children}</div>;
}
